// components/ComparativeAnalysis.jsx
import React from 'react';
import { Waves } from 'lucide-react';

const ComparativeAnalysis = ({ comparisonResults }) => {
  if (!comparisonResults || !comparisonResults.task_transactions1 || !comparisonResults.task_transactions2) {
    return null;
  }

  const firstShipTasks = comparisonResults.task_transactions1;
  const secondShipTasks = comparisonResults.task_transactions2;

  const getAverageCompletion = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const total = tasks.reduce((sum, task) => sum + (task.actual_completion_percent || 0), 0);
    return (total / tasks.length);
  };

  const getTotalActualDays = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    return tasks.reduce((sum, task) => {
      const actualStart = new Date(task.actual_start);
      const actualFinish = new Date(task.actual_finish);
      const diffTime = Math.abs(actualFinish - actualStart);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + (isNaN(diffDays) ? 0 : diffDays);
    }, 0);
  };

  const firstShipAvgCompletion = getAverageCompletion(firstShipTasks);
  const secondShipAvgCompletion = getAverageCompletion(secondShipTasks);
  const firstShipTotalActualDays = getTotalActualDays(firstShipTasks);
  const secondShipTotalActualDays = getTotalActualDays(secondShipTasks);

  const completionDifference = Math.abs(firstShipAvgCompletion - secondShipAvgCompletion).toFixed(2);
  const timeDifference = Math.abs(firstShipTotalActualDays - secondShipTotalActualDays);

  const fasterShip = firstShipTotalActualDays < secondShipTotalActualDays
    ? firstShipTasks[0]?.ship_name || 'First Ship'
    : secondShipTasks[0]?.ship_name || 'Second Ship';

  return (
    <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg border border-blue-100">
      <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
        <Waves className="w-5 h-5 mr-2 text-blue-500" />
        Comparative Analysis
      </h2>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {timeDifference} days
            </div>
            <div className="text-sm text-gray-600">Time Difference</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600 mb-2">
              {completionDifference}%
            </div>
            <div className="text-sm text-gray-600">Completion Gap</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {fasterShip}
            </div>
            <div className="text-sm text-gray-600">More Efficient Ship</div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
          <p className="text-gray-700 text-sm leading-relaxed">
            <strong>Performance Summary:</strong> Based on the current comparison, {
              (firstShipTotalActualDays < secondShipTotalActualDays) ?
              firstShipTasks[0]?.ship_name : secondShipTasks[0]?.ship_name
            } demonstrates superior operational efficiency with faster overall task completion and optimized resource allocation for naval operations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ComparativeAnalysis;